import type { LogWithRelations } from '@owl/shared';
import { useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { type BucketSize, bucketEndIso, formatBucketRange } from '../../../lib/buckets';
import { formatTime } from '../../../lib/time';
import { MessageTypeChip } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

// Logs contained in one bar of the activity chart. Opened by tapping a bucket.
export function BucketLogsSheet({
  ctx,
  fromMs,
  size,
  applicationId,
  applicationIds,
}: {
  ctx: MobileShellCtx;
  fromMs: number;
  size: BucketSize;
  applicationId?: string;
  applicationIds?: string[];
}) {
  const [items, setItems] = useState<LogWithRelations[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const appScopeKey = applicationIds?.join(',') ?? '';
  const from = new Date(fromMs).toISOString();
  const to = bucketEndIso(fromMs, size);

  // biome-ignore lint/correctness/useExhaustiveDependencies: applicationIds is tracked via appScopeKey
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const r = await api.listLogs({
          applicationId,
          applicationIds,
          environment: applicationId ? undefined : ctx.environment,
          from,
          to,
          limit: 50,
        });
        if (cancelled) return;
        setItems(r.items);
        setCursor(r.nextCursor);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationId, appScopeKey, ctx.environment, from, to]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.listLogs({
        applicationId,
        applicationIds,
        environment: applicationId ? undefined : ctx.environment,
        from,
        to,
        cursor,
        limit: 50,
      });
      setItems((prev) => [...(prev ?? []), ...r.items]);
      setCursor(r.nextCursor);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <BottomSheet title={`Logs · ${formatBucketRange(fromMs, size)}`} onClose={ctx.closeSheet}>
      {err && <div className="m-error-banner">{err}</div>}
      {items === null ? (
        <div className="m-loading">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="m-empty">Sin logs en este intervalo</div>
      ) : (
        <>
          <div className="log-list-m">
            {items.map((log) => (
              <button
                type="button"
                key={log.id}
                className="log-row-m"
                onClick={() => ctx.openSheet({ kind: 'log', logId: log.id })}
              >
                <span className="bar" style={{ background: log.messageType.color }} />
                <div className="body">
                  <div className="top">
                    <MessageTypeChip mt={log.messageType} dense />
                    <span className="time">{formatTime(log.timestamp)}</span>
                    {!applicationId && <span className="app">{log.application.name}</span>}
                  </div>
                  <div className="msg">{log.message}</div>
                </div>
              </button>
            ))}
          </div>
          {cursor && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                width: '100%',
                marginTop: 10,
                padding: 12,
                border: '1px solid var(--border-1)',
                background: 'var(--bg-2)',
                borderRadius: 'var(--radius-card)',
                color: 'var(--fg-2)',
                fontSize: 13,
              }}
            >
              {loadingMore ? 'Cargando…' : 'Cargar más'}
            </button>
          )}
        </>
      )}
    </BottomSheet>
  );
}
